/**
 * Optimistic lat/lng + display-field patch for public directory infinite caches.
 * Shared by screen-level `useShopsDirectoryRealtimeSync` and app-shell
 * `CustomerDirectoryGlobalSync` so browse/discovery map pins and list cards
 * update instantly on `customer_directory_updated` SSE events.
 */

import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type { LocalizedNameMap } from 'pi-kiosk-shared';
import type { PublicDiscoverySearchResultDTO } from '../../features/discovery/publicDiscoveryTypes.js';
import type { PublicCustomerSalesPointsPage } from '../../features/shops/publicSalesPointsApi.js';
import {
  isOnlineOnlyCustomerPresence,
  parseSalesPointCustomerPresence,
  type SalesPointCustomerPresence,
} from '../../features/shops/customerPresence.js';

type SalesPointsInfiniteCache = InfiniteData<PublicCustomerSalesPointsPage, string | null>;
type DiscoverySearchInfiniteCache = InfiniteData<PublicDiscoverySearchResultDTO, string | null>;

export interface DirectoryDisplayPatch {
  name?: string;
  location?: string | null;
  description?: string | null;
  nameLocales?: LocalizedNameMap | null;
  descriptionLocales?: LocalizedNameMap | null;
  customerPresence?: SalesPointCustomerPresence;
}

function salesPointsCacheHasSalesPoint(
  oldData: SalesPointsInfiniteCache,
  salesPointId: number,
): boolean {
  return oldData.pages.some((page) =>
    page.items.some((item) => item.salesPointId === salesPointId),
  );
}

function discoveryCacheHasSalesPoint(
  oldData: DiscoverySearchInfiniteCache,
  salesPointId: number,
): boolean {
  return oldData.pages.some((page) =>
    page.items.some((item) => item.salesPointContext.salesPointId === salesPointId),
  );
}

function isSalesPointsInfiniteData(value: unknown): value is SalesPointsInfiniteCache {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const pages = (value as { pages?: unknown }).pages;
  return Array.isArray(pages);
}

function isDiscoverySearchInfiniteData(value: unknown): value is DiscoverySearchInfiniteCache {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const pages = (value as { pages?: unknown }).pages;
  return Array.isArray(pages);
}

function patchSalesPointGeoInSalesPointsPages(
  oldData: SalesPointsInfiniteCache,
  salesPointId: number,
  lat: number | null,
  lng: number | null,
): SalesPointsInfiniteCache {
  return {
    ...oldData,
    pages: oldData.pages.map((page) => ({
      ...page,
      items: page.items.map((item) =>
        item.salesPointId === salesPointId ? { ...item, lat, lng } : item,
      ),
    })),
  };
}

function patchSalesPointGeoInDiscoveryPages(
  oldData: DiscoverySearchInfiniteCache,
  salesPointId: number,
  lat: number | null,
  lng: number | null,
): DiscoverySearchInfiniteCache {
  return {
    ...oldData,
    pages: oldData.pages.map((page) => ({
      ...page,
      items: page.items.map((item) =>
        item.salesPointContext.salesPointId === salesPointId
          ? {
              ...item,
              salesPointContext: {
                ...item.salesPointContext,
                lat,
                lng,
              },
            }
          : item,
      ),
    })),
  };
}

function patchSalesPointDisplayInSalesPointsPages(
  oldData: SalesPointsInfiniteCache,
  salesPointId: number,
  fields: DirectoryDisplayPatch,
): SalesPointsInfiniteCache {
  return {
    ...oldData,
    pages: oldData.pages.map((page) => ({
      ...page,
      items: page.items.map((item) => {
        if (item.salesPointId !== salesPointId) {
          return item;
        }
        return {
          ...item,
          ...(fields.name !== undefined ? { name: fields.name } : {}),
          ...(fields.location !== undefined ? { location: fields.location } : {}),
          ...(fields.description !== undefined ? { description: fields.description } : {}),
          ...(fields.nameLocales !== undefined ? { nameLocales: fields.nameLocales } : {}),
          ...(fields.descriptionLocales !== undefined
            ? { descriptionLocales: fields.descriptionLocales }
            : {}),
          ...(fields.customerPresence !== undefined
            ? { customerPresence: fields.customerPresence }
            : {}),
          ...(fields.customerPresence === 'ONLINE_ONLY' || fields.location === null
            ? { lat: null, lng: null, location: null }
            : {}),
        };
      }),
    })),
  };
}

function patchSalesPointDisplayInDiscoveryPages(
  oldData: DiscoverySearchInfiniteCache,
  salesPointId: number,
  fields: DirectoryDisplayPatch,
): DiscoverySearchInfiniteCache {
  const forceNullGeo =
    fields.customerPresence === 'ONLINE_ONLY' || fields.location === null;
  return {
    ...oldData,
    pages: oldData.pages.map((page) => ({
      ...page,
      items: page.items.map((item) => {
        if (item.salesPointContext.salesPointId !== salesPointId) {
          return item;
        }
        if (fields.name === undefined && !forceNullGeo) {
          return item;
        }
        return {
          ...item,
          salesPointContext: {
            ...item.salesPointContext,
            ...(fields.name !== undefined ? { name: fields.name } : {}),
            // A13a — discovery pins must drop when ONLINE_ONLY / public location null.
            ...(forceNullGeo ? { lat: null, lng: null } : {}),
          },
        };
      }),
    })),
  };
}

function parseDirectoryGeoCoordinate(value: unknown): number | null | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (value === null) {
    return null;
  }
  return undefined;
}

/**
 * True when SSE payload is ONLINE_ONLY or public location is explicitly null (A13a).
 * Callers must force-null geo and never rehydrate ops location into caches.
 * Does **not** invent ONLINE_ONLY from null location alone (D1).
 */
export function isDirectoryPayloadOnlineOnlyOrNullLocation(
  payload: Record<string, unknown> | undefined,
): boolean {
  if (payload === undefined) {
    return false;
  }
  if (isOnlineOnlyCustomerPresence(parseSalesPointCustomerPresence(payload.customerPresence))) {
    return true;
  }
  return payload.location === null;
}

/** Read lat/lng from a `customer_directory_updated` payload when both are present. */
export function readDirectoryGeoFromPayload(
  payload: Record<string, unknown> | undefined,
): { lat: number | null; lng: number | null } | null {
  if (payload === undefined) {
    return null;
  }
  // A13a — ONLINE_ONLY / null public location always patch null geo (never rehydrate).
  if (isDirectoryPayloadOnlineOnlyOrNullLocation(payload)) {
    return { lat: null, lng: null };
  }
  const lat = parseDirectoryGeoCoordinate(payload.lat);
  const lng = parseDirectoryGeoCoordinate(payload.lng);
  if (lat === undefined || lng === undefined) {
    return null;
  }
  return { lat, lng };
}

/** Parse salesPointId from a `customer_directory_updated` payload. */
export function readDirectorySalesPointId(
  payload: Record<string, unknown> | undefined,
): number | null {
  if (payload === undefined) {
    return null;
  }
  const salesPointIdRaw = payload.salesPointId;
  if (typeof salesPointIdRaw === 'number' && Number.isFinite(salesPointIdRaw)) {
    return salesPointIdRaw;
  }
  if (typeof salesPointIdRaw === 'string' && salesPointIdRaw.trim().length > 0) {
    const parsed = Number(salesPointIdRaw);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
}

/** Read display fields from a `customer_directory_updated` payload when present. */
export function readDirectoryDisplayFieldsFromPayload(
  payload: Record<string, unknown> | undefined,
): DirectoryDisplayPatch | null {
  if (payload === undefined) {
    return null;
  }
  const presence = parseSalesPointCustomerPresence(payload.customerPresence);
  const onlineOnly = isOnlineOnlyCustomerPresence(presence);
  const patch: DirectoryDisplayPatch = {};
  if (typeof payload.name === 'string') {
    patch.name = payload.name;
  }
  if (onlineOnly) {
    // A13a — never rehydrate ops address into Obchody caches for ONLINE_ONLY.
    patch.location = null;
    patch.customerPresence = 'ONLINE_ONLY';
  } else if (payload.location === null || typeof payload.location === 'string') {
    // Null public location: write null (never invent ops text); do not invent ONLINE_ONLY (D1).
    patch.location = payload.location as string | null;
  }
  if (payload.customerPresence !== undefined && !onlineOnly) {
    patch.customerPresence = presence;
  }
  if (payload.description === null || typeof payload.description === 'string') {
    patch.description = payload.description as string | null;
  }
  if (payload.nameLocales === null || typeof payload.nameLocales === 'object') {
    patch.nameLocales = payload.nameLocales as LocalizedNameMap | null;
  }
  if (
    payload.descriptionLocales === null ||
    typeof payload.descriptionLocales === 'object'
  ) {
    patch.descriptionLocales = payload.descriptionLocales as LocalizedNameMap | null;
  }
  return Object.keys(patch).length > 0 ? patch : null;
}

/**
 * Optimistically patch lat/lng for one sales point across public directory caches.
 * Returns true when at least one loaded cache page already contained the sales point.
 */
export function patchDirectoryGeoInCaches(
  queryClient: QueryClient,
  apiBaseUrl: string,
  salesPointId: number,
  lat: number | null,
  lng: number | null,
): boolean {
  let applied = false;
  const salesPointsPrefix = ['public', 'customer-sales-points', apiBaseUrl] as const;
  queryClient.setQueriesData<unknown>(
    { queryKey: salesPointsPrefix },
    (oldData: unknown) => {
      if (!isSalesPointsInfiniteData(oldData)) {
        return oldData;
      }
      if (!salesPointsCacheHasSalesPoint(oldData, salesPointId)) {
        return oldData;
      }
      applied = true;
      return patchSalesPointGeoInSalesPointsPages(oldData, salesPointId, lat, lng);
    },
  );

  const discoverySearchPrefix = ['public', 'discovery', 'search', apiBaseUrl] as const;
  queryClient.setQueriesData<unknown>(
    { queryKey: discoverySearchPrefix },
    (oldData: unknown) => {
      if (!isDiscoverySearchInfiniteData(oldData)) {
        return oldData;
      }
      if (!discoveryCacheHasSalesPoint(oldData, salesPointId)) {
        return oldData;
      }
      applied = true;
      return patchSalesPointGeoInDiscoveryPages(oldData, salesPointId, lat, lng);
    },
  );
  return applied;
}

/** Optimistically patch name/address/description across public directory caches. */
export function patchDirectoryDisplayInCaches(
  queryClient: QueryClient,
  apiBaseUrl: string,
  salesPointId: number,
  fields: DirectoryDisplayPatch,
): void {
  const salesPointsPrefix = ['public', 'customer-sales-points', apiBaseUrl] as const;
  queryClient.setQueriesData<unknown>(
    { queryKey: salesPointsPrefix },
    (oldData: unknown) => {
      if (!isSalesPointsInfiniteData(oldData)) {
        return oldData;
      }
      return patchSalesPointDisplayInSalesPointsPages(oldData, salesPointId, fields);
    },
  );

  const discoverySearchPrefix = ['public', 'discovery', 'search', apiBaseUrl] as const;
  queryClient.setQueriesData<unknown>(
    { queryKey: discoverySearchPrefix },
    (oldData: unknown) => {
      if (!isDiscoverySearchInfiniteData(oldData)) {
        return oldData;
      }
      return patchSalesPointDisplayInDiscoveryPages(oldData, salesPointId, fields);
    },
  );
}
